import { useRef, useEffect, useCallback } from 'react';
import { List } from 'react-window';
import ResultCard from './ResultCard';
import ResultsListSkeleton from './ResultsListSkeleton';
import ResultsListEmpty from './ResultsListEmpty';
import ResultsListError from './ResultsListError';

const VIRTUALIZE_THRESHOLD = 50;
const ROW_HEIGHT = 220;

/**
 * List of search result cards. Scrolls highlighted item into view when map pin is hovered.
 * When items > 50, uses react-window List for virtualization.
 */
export default function ResultsList({
  items = [],
  isParent,
  getAvatarUrl,
  getInitials,
  selectedItemId,
  hoveredItemId,
  onSelectItem,
  onContactClick,
  onHoverItem,
  loading,
  error,
  onRetry,
  hasMoreResults,
  loadingMore,
  onLoadMore,
  emptyInBounds = false,
  emptyVariant = 'default',
  emptyVariantProps,
  listHeight = 480,
  scrollToHovered = true,
}) {
  const cardRefs = useRef({});
  const listRef = useRef(null);

  // When hoveredItemId changes (from map pin hover), scroll that card into view (only if scrollToHovered is true)
  useEffect(() => {
    if (!scrollToHovered || !hoveredItemId || !items.length) return;
    const index = items.findIndex((i) => i.id === hoveredItemId);
    if (index >= 0 && listRef.current?.scrollToRow) {
      listRef.current.scrollToRow({ index, align: 'smart', behavior: 'smooth' });
    } else if (cardRefs.current[hoveredItemId]) {
      cardRefs.current[hoveredItemId].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [hoveredItemId, items, scrollToHovered]);

  // When selectedItemId changes (e.g. click on map pin), scroll that card into view so result is shown nicely
  useEffect(() => {
    if (!selectedItemId || !items.length) return;
    const index = items.findIndex((i) => i.id === selectedItemId);
    if (index >= 0 && listRef.current?.scrollToRow) {
      listRef.current.scrollToRow({ index, align: 'center', behavior: 'smooth' });
    } else if (cardRefs.current[selectedItemId]) {
      cardRefs.current[selectedItemId].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedItemId, items]);

  // Must be called unconditionally (Rules of Hooks) — before any early return
  const RowComponent = useCallback(
    ({ index, style }) => {
      const item = items[index];
      if (!item) return null;
      return (
        <div style={style} className="px-0.5 pb-2">
          <div
            ref={(el) => { cardRefs.current[item.id] = el; }}
            className=""
          >
            <ResultCard
              item={item}
              isParent={isParent}
              avatarUrl={getAvatarUrl?.(item) ?? (item.photoUrl || item.photo_url || item.profileImage)}
              initials={getInitials?.(item) ?? (isParent ? `${item.firstName?.[0] || ''}${item.lastName?.[0] || ''}`.toUpperCase() : 'P')}
              isSelected={selectedItemId === item.id}
              isHovered={hoveredItemId === item.id}
              onMouseEnter={() => onHoverItem?.(item.id)}
              onMouseLeave={() => onHoverItem?.(null)}
              onClick={() => onSelectItem?.(item)}
              onContactClick={onContactClick}
            />
          </div>
        </div>
      );
    },
    [items, isParent, getAvatarUrl, getInitials, selectedItemId, hoveredItemId, onHoverItem, onSelectItem, onContactClick]
  );

  if (loading) {
    return <ResultsListSkeleton count={6} />;
  }

  if (error) {
    return <ResultsListError message={error} onRetry={onRetry} />;
  }

  if (items.length === 0) {
    return (
      <ResultsListEmpty
        inBounds={emptyInBounds}
        variant={emptyVariant}
        {...emptyVariantProps}
      />
    );
  }

  const useVirtualList = items.length > VIRTUALIZE_THRESHOLD;

  if (useVirtualList) {
    return (
      <div className="virtual-list-container">
        <List
          listRef={listRef}
          rowComponent={RowComponent}
          rowCount={items.length}
          rowHeight={ROW_HEIGHT}
          rowProps={{}}
          overscanCount={4}
          style={{ height: listHeight, width: '100%' }}
        />
        {hasMoreResults && (
          <div className="pt-2 pb-2 flex justify-center border-t border-gray-100 dark:border-gray-700">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              className="px-5 py-2.5 rounded-xl border-2 border-primary-200 dark:border-primary-700 bg-white dark:bg-gray-800 text-primary-700 dark:text-primary-300 text-sm font-medium hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50"
            >
              {loadingMore ? 'Učitavanje…' : 'Učitaj još'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div
          key={item.id}
          ref={(el) => { cardRefs.current[item.id] = el; }}
          className=""
        >
          <ResultCard
            item={item}
            isParent={isParent}
            avatarUrl={getAvatarUrl?.(item) ?? (item.photoUrl || item.photo_url || item.profileImage)}
            initials={getInitials?.(item) ?? (isParent ? `${item.firstName?.[0] || ''}${item.lastName?.[0] || ''}`.toUpperCase() : 'P')}
            isSelected={selectedItemId === item.id}
            isHovered={hoveredItemId === item.id}
            onMouseEnter={() => onHoverItem?.(item.id)}
            onMouseLeave={() => onHoverItem?.(null)}
            onClick={() => onSelectItem?.(item)}
          />
        </div>
      ))}
      {hasMoreResults && (
        <div className="pt-2 pb-2 flex justify-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="px-5 py-2.5 rounded-xl border-2 border-primary-200 dark:border-primary-700 bg-white dark:bg-gray-800 text-primary-700 dark:text-primary-300 text-sm font-medium hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50 transition-colors"
          >
            {loadingMore ? 'Učitavanje…' : 'Učitaj još'}
          </button>
        </div>
      )}
    </div>
  );
}
